import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { ALPHA, CHART_REFERENCE } from "../../../theme/tokens";
import type { ManagementCenterHoursResponse } from "../../../utils/models";
import { type ClampRange, fetchedDataToChartDataGroup, getCensusYDomain } from "../utils";

type Props = {
	data?: ManagementCenterHoursResponse;
	isLoading: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const HoursOverTime: FC<Props> = ({ data, isLoading, trailing, containerSx, shrinkLegendForSmallScreens, clampRange }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const [searchParams] = useSearchParams();

	const handleClick = () => {
		const params = new URLSearchParams(searchParams);
		params.set("metric", "hours");
		track("schedule_vs_actual_report_opened", { metric: "hours", department_id: chosenDepartment.department_id });
		navigate(`/${chosenDepartment.department_id}/schedule-vs-actual?${params.toString()}`);
	};

	const hasTarget = data && Object.keys(data.targetHours).length > 0;

	const transformedDataLeft =
		!isLoading && data
			? [
					fetchedDataToChartDataGroup({
						data: data.hoursScheduled,
						name: "SCHEDULED HOURS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.SCHEDULED,
					}),
					fetchedDataToChartDataGroup({
						data: data.hoursActual,
						name: "ACTUAL HOURS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.ACTUAL,
					}),
					...(hasTarget
						? [
								fetchedDataToChartDataGroup({
									data: data.targetHours,
									name: "TARGET",
									timezone,
									clampRange,
									color: CHART_REFERENCE.TARGET,
									strokeDasharray: CHART_REFERENCE.TARGET_DASH,
								}),
							]
						: []),
				]
			: undefined;

	const transformedDataRight =
		!isLoading && data && transformedDataLeft
			? [
					fetchedDataToChartDataGroup({
						data: data.census,
						name: "CENSUS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.CENSUS,
					}),
				]
			: undefined;

	const allHoursValues = data ? [...Object.values(data.hoursActual), ...Object.values(data.hoursScheduled)] : [];
	if (hasTarget) {
		allHoursValues.push(...Object.values(data.targetHours));
	}
	const maxHours = allHoursValues.length > 0 ? Math.ceil(Math.max(...allHoursValues) * 1.05) : 0;
	const minHours = allHoursValues.length > 0 ? Math.max(Math.floor(Math.min(...allHoursValues) * 0.95), 0) : 0;
	const censusDomain = getCensusYDomain(data?.census);

	return (
		<CommonDashboardBox
			onClick={handleClick}
			sx={
				((theme) => ({
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: 1,
					width: "calc(33% - 4px)",
					...containerSx,
					cursor: "pointer",
					"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
					transition: "box-shadow 0.2s",
				})) as SxProps<Theme>
			}
		>
			<CommonDashboardHeader label={"Hours Over Time"} trailing={trailing} />
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				margin={{
					top: 4,
					right: 0,
					bottom: 0,
					left: 20,
				}}
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				leftAxisData={
					transformedDataLeft
						? {
								dataGroups: transformedDataLeft,
								maxValue: maxHours,
								minValue: minHours,
								tickRenderer: (value) => {
									return `${value.toFixed(0)}h`;
								},
							}
						: undefined
				}
				rightAxisData={
					transformedDataRight
						? {
								dataGroups: transformedDataRight,
								maxValue: censusDomain?.max,
								minValue: censusDomain?.min,
								tickRenderer: (value) => {
									return `${value}${value === censusDomain?.max ? "🧑" : ""}`;
								},
							}
						: undefined
				}
				tooltipSortOrder={{ "SCHEDULED HOURS": 0, "ACTUAL HOURS": 1, TARGET: 2, CENSUS: 3 }}
			/>
		</CommonDashboardBox>
	);
};
